import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { OperatingArea } from '@freelancer/datastore/collections';
import { UserAvailabilityFilters } from '@freelancer/search-filters';
import { trackById } from '@freelancer/ui';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, Observable } from 'rxjs';
import type { ChecklistOption } from '../../search-checklist-filter/search-checklist-filter.types';
import type {
  UserAvailabilityPreferredDropOffFormType,
  UserAvailabilityPreferredPickupFormType,
  UserAvailabilitySearchFiltersFormType,
} from '../search-user-availabilities.model';

@UntilDestroy({ className: 'SearchUserAvailabilitiesFiltersComponent' })
@Component({
  selector: 'app-search-user-availabilities-filters',
  template: `
    <fl-grid class="Filters">
      <fl-col
        [col]="12"
        [colTablet]="8"
      >
        <fl-bit class="Filters-locationDropdownContainer">
          <fl-bit class="Filters-locationDropdown">
            <fl-label
              i18n="Preferred pickup filter label"
              [color]="labelFontColor"
              [for]="'PreferredPickupArea'"
              [sizeTablet]="TextSize.XSMALL"
              [size]="TextSize.SMALL"
              [weight]="FontWeight.BOLD"
            >
              Preferred pickup
            </fl-label>
            <app-freightlancer-search-filters-loads-location
              flTrackingSection="PreferredPickupAreaDropdownFilter"
              [buttonTextApplied]="preferredPickupAreasAppliedText$ | flAsync"
              [dropdownFilterId]="'PreferredPickupArea'"
              [flMarginBottomTablet]="'none'"
              [flMarginBottom]="'mid'"
              [locationFormGroup]="preferredPickupAreasFormGroup"
              [operatingAreas$]="operatingAreas$"
            >
            </app-freightlancer-search-filters-loads-location>
          </fl-bit>

          <fl-icon
            class="Filters-switchLocationsIcon"
            flTrackingLabel="SwitchLocationsIcon"
            [clickable]="true"
            [color]="IconColor.PRIMARY"
            [flHideMobile]="true"
            [name]="'ui-switch'"
            [size]="IconSize.SMALL"
            (click)="handleSwitchLocationsButtonClick()"
          ></fl-icon>

          <fl-bit class="Filters-locationDropdown">
            <fl-label
              i18n="Preferred dropoff filter label"
              [color]="labelFontColor"
              [sizeTablet]="TextSize.XSMALL"
              [size]="TextSize.SMALL"
              [weight]="FontWeight.BOLD"
              [for]="'PreferredDropOffArea'"
            >
              Preferred dropoff
            </fl-label>
            <app-freightlancer-search-filters-loads-location
              flTrackingSection="PreferredDropOffAreaDropdownFilter"
              [buttonTextApplied]="preferredDropOffAreasAppliedText$ | flAsync"
              [dropdownFilterId]="'PreferredDropOffArea'"
              [flMarginBottomTablet]="'none'"
              [flMarginBottom]="'mid'"
              [locationFormGroup]="preferredDropOffAreasFormGroup"
              [operatingAreas$]="operatingAreas$"
            >
            </app-freightlancer-search-filters-loads-location>
          </fl-bit>
        </fl-bit>
      </fl-col>
      <fl-col
        [col]="12"
        [colTablet]="4"
      >
        <fl-bit class="Filters-vehicleTypeDropdownContainer">
          <fl-label
            i18n="Vehicle type filter label"
            [color]="labelFontColor"
            [for]="'VehicleType'"
            [sizeTablet]="TextSize.XSMALL"
            [size]="TextSize.SMALL"
            [weight]="FontWeight.BOLD"
          >
            Vehicle type
          </fl-label>
          <fl-dropdown-filter
            buttonText="All vehicle types"
            class="Filters-vehicleTypeDropdown"
            i18n-buttonText="Vehicle type dropdown default button text"
            [buttonTextApplied]="vehicleTypeButtonTextApplied$ | flAsync"
            [filterApplied]="
              (vehicleTypeButtonTextApplied$ | flAsync) !== undefined
            "
            [id]="'VehicleType'"
          >
            <fl-bit
              *ngIf="vehicleTypeChecklistOptions$ | flAsync as checklistOptions"
              class="Filters-vehicleTypeDropdown-checkboxContainer"
            >
              <ng-container
                *ngFor="let option of checklistOptions; trackBy: trackById"
              >
                <fl-checkbox
                  class="Filters-vehicleTypeDropdown-checkbox"
                  flTrackingLabel="VehicleTypeDropdownCheckbox"
                  [control]="option.control"
                  [flMarginBottom]="'small'"
                  [label]="option.displayValue"
                ></fl-checkbox>
              </ng-container>
            </fl-bit>
          </fl-dropdown-filter>
        </fl-bit>
      </fl-col>
    </fl-grid>
  `,
  styleUrls: ['./search-user-availabilities-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchUserAvailabilitiesFiltersComponent {
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  TextSize = TextSize;
  trackById = trackById;

  @Input() labelFontColor: FontColor = FontColor.LIGHT;
  @Input() filters: FormGroup<UserAvailabilitySearchFiltersFormType>;
  @Input() operatingAreas$: Observable<readonly OperatingArea[]>;
  @Input() operatingAreaChecklistOptions$: Observable<
    readonly ChecklistOption[]
  >;
  @Input() preferredDropOffAreasAppliedText$: Observable<string | undefined>;
  @Input()
  preferredDropOffAreasFormGroup: FormGroup<UserAvailabilityPreferredDropOffFormType>;
  @Input() preferredPickupAreasAppliedText$: Observable<string | undefined>;
  @Input()
  preferredPickupAreasFormGroup: FormGroup<UserAvailabilityPreferredPickupFormType>;
  @Input() vehicleTypeButtonTextApplied$: Observable<string | undefined>;
  @Input() vehicleTypeChecklistOptions$: Observable<readonly ChecklistOption[]>;

  handleSwitchLocationsButtonClick(): void {
    const tempPickupFormValues =
      this.filters.controls[UserAvailabilityFilters.PREFERRED_PICKUP_AREAS]
        .value;
    const tempDropOffFormValues =
      this.filters.controls[UserAvailabilityFilters.PREFERRED_DROPOFF_AREAS]
        .value;

    firstValueFrom(
      this.operatingAreaChecklistOptions$.pipe(untilDestroyed(this)),
    ).then(options => {
      options.forEach(option => {
        this.preferredPickupAreasFormGroup
          .get(option.id)
          ?.setValue(
            tempDropOffFormValues.find(
              (value: number) => value.toString() === option.id,
            ),
          );
        this.preferredDropOffAreasFormGroup
          .get(option.id)
          ?.setValue(
            tempPickupFormValues.find(
              (value: number) => value.toString() === option.id,
            ),
          );
      });
    });
  }
}
